import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { theme } from "@polifonia/theme";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import type { ModalProps } from "@/components/Modal";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import {
  API_KEY_NAME_MAX_LENGTH,
  validateApiKeyName,
} from "@/pages/applications/pages/apiKeys/utils/apiKeyName";
import { isHandledGlobally } from "@/pages/applications/pages/apiKeys/utils/isHandledGlobally";
import { maskApiKey } from "@/pages/applications/pages/apiKeys/utils/maskApiKey";
import { sharedStyles } from "@/pages/applications/pages/customFields/components/sharedStyles";

type ApiKey = InterludeTypes["ApiKey"];

interface FormValues {
  name: string;
}

interface RenameApiKeyModalProps {
  open: boolean;
  apiKey: ApiKey | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const RenameApiKeyModal: React.FC<RenameApiKeyModalProps> =
  function RenameApiKeyModal({ open, apiKey, onClose, onSuccess }) {
    const { t } = useTranslation();
    const [apiError, setApiError] = useState<string | null>(null);

    const { control, handleSubmit, reset, watch } = useForm<FormValues>({
      mode: "onChange",
      defaultValues: { name: "" },
    });

    const watchedName = watch("name");

    // Seeded from the row, so the field opens on the current name -- including
    // the empty string a legacy or CS-issued key carries.
    useEffect(() => {
      if (apiKey) {
        reset({ name: apiKey.name });
      }
    }, [apiKey, reset]);

    const nameErrorMessages = useMemo(
      () => ({
        required: t("apiKey.error.nameRequired", "Enter a name for this key."),
        tooLong: t("apiKey.error.nameTooLong", "Use at most 128 characters."),
        forbiddenCharacter: t(
          "apiKey.error.nameForbiddenCharacter",
          "Spaces, commas, slashes and control characters are not allowed.",
        ),
      }),
      [t],
    );

    const updateMutation = interlude.apiKey.useUpdateApiKey(
      { params: { credentialId: apiKey?.id ?? "" } },
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
        onError: (error) => {
          // Same rule as the create modal: leave anything the global snackbar
          // already says to it, and speak only for the codeless 422 the name
          // validator produces.
          if (isHandledGlobally(error)) return;
          setApiError(
            t(
              "apiKey.error.renameFailed",
              "The key could not be renamed. Check the name and try again.",
            ),
          );
        },
      },
    );

    const handleClose = useHandler(() => {
      reset({ name: "" });
      setApiError(null);
      onClose();
    });

    const handleModalClose = useHandler<ModalProps["handleClose"]>(
      (_event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        handleClose();
      },
    );

    const onSubmit = useHandler((values: FormValues) => {
      if (!apiKey) return;
      setApiError(null);
      /**
       * Only `name` is sent. `status` is omitted rather than passed through,
       * because interlude reads the body with `exclude_unset=True`: an omitted
       * field is the only way to leave one alone, and echoing the row's status
       * back would overwrite whatever it became in the meantime. There is also no
       * way to send it for an expired key, which has no settable status.
       */
      updateMutation.mutate({ name: values.name });
    });

    const footer: React.ReactNode = useMemo(
      () => (
        <Stack direction="row" justifyContent="flex-end" gap="12px">
          <Button variant="plain" onClick={handleClose}>
            {t("apiKey.renameModal.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={
              !apiKey ||
              validateApiKeyName(watchedName) !== null ||
              watchedName === apiKey.name ||
              updateMutation.isPending
            }
            loading={updateMutation.isPending}
          >
            {t("apiKey.renameModal.submit", "Save")}
          </Button>
        </Stack>
      ),
      [
        apiKey,
        handleClose,
        handleSubmit,
        onSubmit,
        watchedName,
        updateMutation.isPending,
        t,
      ],
    );

    if (!apiKey) {
      return null;
    }

    return (
      <Modal
        open={open}
        handleClose={handleModalClose}
        title={t("apiKey.renameModal.title", "Rename API key")}
        width={520}
        footer={footer}
      >
        <Stack gap="16px" paddingBottom="8px">
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t(
              "apiKey.renameModal.description",
              "Renaming changes only the label shown here. The key itself and anything using it are unaffected.",
            )}
          </Typography>

          {apiError ? (
            <InfoBox
              variant="error"
              title={t(
                "apiKey.error.renameFailed.title",
                "Could not rename the key",
              )}
              description={apiError}
            />
          ) : null}

          <Box sx={sharedStyles.formSection}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgBody}
            >
              {t("apiKey.renameModal.keyLabel", "Key")}
            </Typography>
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {maskApiKey(apiKey.key)}
            </Typography>
          </Box>

          <Box sx={sharedStyles.formSection}>
            <Box sx={sharedStyles.label}>
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgBody}
              >
                {t("apiKey.renameModal.name.label", "Name")}
              </Typography>
              <Box component="span" sx={sharedStyles.required}>
                *
              </Box>
            </Box>
            <Controller
              name="name"
              control={control}
              rules={{
                validate: (value) => {
                  const violation = validateApiKeyName(value);
                  if (!violation) return true;
                  return nameErrorMessages[violation];
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <TextField
                    {...field}
                    fullWidth
                    placeholder={t(
                      "apiKey.createModal.name.placeholder",
                      "order-sync",
                    )}
                    error={!!error}
                    disabled={updateMutation.isPending}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <Box component="span" sx={sharedStyles.charCount}>
                            <Typography variant="note" color="inherit">
                              {watchedName.length}/{API_KEY_NAME_MAX_LENGTH}
                            </Typography>
                          </Box>
                        ),
                      },
                    }}
                  />
                  {error ? (
                    <FormHelperText error>{error.message}</FormHelperText>
                  ) : (
                    <FormHelperText>
                      {t(
                        "apiKey.createModal.name.hint",
                        "Up to 128 characters. Spaces, commas and slashes are not allowed.",
                      )}
                    </FormHelperText>
                  )}
                </>
              )}
            />
          </Box>
        </Stack>
      </Modal>
    );
  };
